import React, { useState, useEffect } from "react";
import { supabase, isSupabaseConfigured } from "./lib/supabase.js";
import TaxPlanningTool from "./components/TaxPlanningTool.jsx";
import SetupNeeded from "./components/auth/SetupNeeded.jsx";

/**
 * TEST MODE — authentication and approval gates removed.
 *
 * Anyone with the URL can access the tool. The app still establishes
 * an anonymous Supabase session so RLS queries pass (every visitor
 * gets a unique random auth user) and the profile row auto-approves
 * via the handle_new_user trigger.
 *
 * To re-enable proper auth + approval: revert this file to the
 * previous version (session/profile loading + Login + PendingApproval)
 * and restore the original handle_new_user trigger so approved
 * defaults to false.
 */
export default function App() {
  if (!isSupabaseConfigured) return <SetupNeeded />;

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function ensureSession() {
      // Reuse an existing session if the browser already has one
      const {
        data: { session: existing },
      } = await supabase.auth.getSession();
      if (existing) {
        if (mounted) {
          setSession(existing);
          setLoading(false);
        }
        return;
      }
      // Otherwise spin up an anonymous session so RLS-backed queries pass
      try {
        const { data, error } = await supabase.auth.signInAnonymously();
        if (error) throw error;
        if (mounted) setSession(data?.session || null);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Anonymous sign-in failed:", e);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    ensureSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      if (mounted) setSession(newSession);
    });

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center sans text-stone-500">
        Loading…
      </div>
    );
  }

  // Bypass the approval gate — treat every visitor as approved for test mode
  const fakeProfile = {
    approved: true,
    is_admin: true,
    email: session?.user?.email || "test@linkedaccounting.com",
    display_name: session?.user?.email?.split("@")[0] || "Guest",
  };

  return <TaxPlanningTool session={session} profile={fakeProfile} />;
}
